import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControlLabel,
  Checkbox,
  Alert,
} from '@mui/material';
import type { BaseCalculoPlanilla } from '../types/formula';
import { initialBaseCalculo } from '../types/formula';
import { useFormulaStore } from '../store/formulaStore';
import FormulaEditor from './FormulaEditor';

interface EditBaseCalculoProps {
  open: boolean;
  onClose: () => void;
}

const EditBaseCalculo: React.FC<EditBaseCalculoProps> = ({ open, onClose }) => {
  const { selectedBase, saveBaseCalculo, loading, error, clearError } = useFormulaStore();
  const [formData, setFormData] = useState<BaseCalculoPlanilla>({ ...initialBaseCalculo });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      setFormData(selectedBase ? { ...selectedBase } : { ...initialBaseCalculo });
      setErrors({});
      clearError();
    }
  }, [open, selectedBase, clearError]);

  const handleChange = (field: keyof BaseCalculoPlanilla, value: unknown) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.clave.trim()) newErrors.clave = 'La clave es requerida';
    if (!formData.nombre.trim()) newErrors.nombre = 'El nombre es requerido';
    if (!formData.formula.trim()) newErrors.formula = 'La fórmula es requerida';
    if (!/^[A-Z][A-Z0-9_]*$/.test(formData.clave)) {
      newErrors.clave = 'Solo mayúsculas, números y guiones bajos. Debe iniciar con letra.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    await saveBaseCalculo(formData);
  };

  const isEditing = !!selectedBase;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEditing ? 'Editar Base de Cálculo' : 'Nueva Base de Cálculo'}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Clave"
              value={formData.clave}
              onChange={(e) => handleChange('clave', e.target.value.toUpperCase())}
              error={!!errors.clave}
              helperText={errors.clave || 'Ej: BASE_PENSIONES'}
              fullWidth
              size="small"
              slotProps={{ htmlInput: { style: { textTransform: 'uppercase' } } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 5 }}>
            <TextField
              label="Nombre"
              value={formData.nombre}
              onChange={(e) => handleChange('nombre', e.target.value)}
              error={!!errors.nombre}
              helperText={errors.nombre}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 1.5 }}>
            <TextField
              label="Orden"
              type="number"
              value={formData.orden}
              onChange={(e) => handleChange('orden', Number(e.target.value))}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 1.5 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.esBaseImponible}
                  onChange={(e) => handleChange('esBaseImponible', e.target.checked)}
                />
              }
              label="Base Imp."
              sx={{ mt: -0.5 }}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              label="Descripción"
              value={formData.descripcion || ''}
              onChange={(e) => handleChange('descripcion', e.target.value)}
              fullWidth
              size="small"
              multiline
              minRows={2}
            />
          </Grid>
          <Grid size={12}>
            <FormulaEditor
              value={formData.formula}
              onChange={(formula) => handleChange('formula', formula)}
              error={!!errors.formula}
              helperText={errors.formula}
            />
          </Grid>
          <Grid size={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.activo}
                  onChange={(e) => handleChange('activo', e.target.checked)}
                />
              }
              label="Activo"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancelar</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
          {loading ? 'Guardando...' : isEditing ? 'Guardar Cambios' : 'Crear'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditBaseCalculo;
